import { Link } from 'react-router-dom';
import { Button, ConfirmDialog, HookedForm, InputField, Loading, TemplateDesignEditor } from '@app/components';
import ArrowLeft from '@app/assets/icons/arrow-left-light-gray.svg?react';
import EmailIcon from '@app/assets/icons/mail-box.svg?react';
import IPhoneIcon from '@app/assets/icons/iphone.svg?react';
import { EmailDesignContent, MessageDesignContent } from '@app/models/messageTemplates';
import { MessageTemplateChannel, MessageTemplateChannelLabels } from '@app/utils/consts';
import { MessageTemplateFormValues, useMessageTemplateEdit } from './MessageTemplateEdit.hooks';

const FORM_ID = 'message-template-form';

const ChannelBadge = ({ channel }: { channel: MessageTemplateChannel }) => {
  const Icon = channel === MessageTemplateChannel.EMAIL ? EmailIcon : IPhoneIcon;

  return (
    <span className={`flex items-center gap-1.5  ${channel === MessageTemplateChannel.SMS ? 'bg-accent-orange/40' : 'bg-gradient-ufoGreen/40'} text-base-raisinBlack text-xs px-2 py-1 rounded-full font-medium shrink-0`}>
      <Icon className="w-4 h-4" />
      {MessageTemplateChannelLabels[channel]}
    </span>
  );
};

const PageMessage = ({ title, description }: { title: string; description: string }) => (
  <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-2 mt-40 text-center">
    <p className="font-bold">{title}</p>
    <p className="text-sm text-base-coolGray">{description}</p>
    <Link to="/templates" className="text-sm text-brand-baseBlue font-medium">
      Back to templates
    </Link>
  </div>
);

export const MessageTemplateEdit = () => {
  const {
    form,
    isCreate,
    canEdit,
    template,
    isLoading,
    isNotFound,
    loadError,
    usage,
    usageCount,
    channel,
    content,
    setContent,
    contentErrors,
    onSubmit,
    isSaving,
    cancel,
    blocker,
  } = useMessageTemplateEdit();

  if (!canEdit) {
    return <PageMessage title="No access" description="You need permission to create campaigns or automations to manage templates." />;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (isNotFound) {
    return <PageMessage title="Template not found" description="It may have been deleted." />;
  }

  if (loadError) {
    return <PageMessage title="Couldn't load the template" description="Please try again later." />;
  }

  const channelName = channel === MessageTemplateChannel.SMS ? 'SMS' : 'email';

  return (
    <>
      <div className="bg-white border-b border-b-base-ghostWhite">
        <div className="container mx-auto py-4 px-4 flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={cancel} aria-label="Back to templates" className="shrink-0">
              <ArrowLeft />
            </button>
            <h2 className="font-bold text-[28px] truncate">{isCreate ? 'Create template' : template?.name}</h2>
            <ChannelBadge channel={channel} />
            {template?.isDefault && <span className="bg-brand-baseBlue text-white text-xs px-2 py-1 rounded-full font-medium shrink-0">Default</span>}
          </div>
          <div className="flex gap-2">
            <Button kind="secondary" type="button" onClick={cancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" form={FORM_ID} disabled={isSaving}>
              {isSaving ? 'Saving...' : isCreate ? 'Create template' : 'Save changes'}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-6 px-4 space-y-6">
        {/* The design editors contain plain buttons, so they're kept outside the <form> to avoid accidental submits */}
        <HookedForm<MessageTemplateFormValues> id={FORM_ID} formHook={form} onSubmit={(values) => onSubmit(values)}>
          <InputField name="name" label="Template name" placeholder="e.g. Spring offer" containerClass="max-w-md" maxLength={191} />
        </HookedForm>

        {!isCreate && usage && usageCount > 0 && (
          <div className="bg-brand-lightBlue4 rounded-lg p-4 text-sm text-base-davyGray">
            Changes apply to everything that uses this template:
            {usage.automations.length > 0 && ` ${usage.automations.length} automation${usage.automations.length > 1 ? 's' : ''}`}
            {usage.automations.length > 0 && usage.campaigns.length > 0 && ' and'}
            {usage.campaigns.length > 0 && ` ${usage.campaigns.length} campaign${usage.campaigns.length > 1 ? 's' : ''}`}.
          </div>
        )}

        {template?.isDefault && (
          <div className="bg-brand-lightBlue4 rounded-lg p-4 text-sm text-base-davyGray">This is the default {channelName} template. Automations and campaigns without a selected template use it.</div>
        )}

        {contentErrors.length > 0 && (
          <ul role="alert" className="text-system-error text-sm space-y-1">
            {contentErrors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )}

        {channel === MessageTemplateChannel.EMAIL ? (
          <TemplateDesignEditor channel={MessageTemplateChannel.EMAIL} value={content as EmailDesignContent} onChange={setContent} className="h-[calc(100vh-320px)] min-h-[560px]" />
        ) : (
          <TemplateDesignEditor channel={MessageTemplateChannel.SMS} value={content as MessageDesignContent} onChange={setContent} className="h-[calc(100vh-320px)] min-h-[560px]" />
        )}
      </div>

      <ConfirmDialog
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
        title="Leave without saving?"
        message="You have unsaved changes to this template. They will be lost if you leave this page."
        confirmText="Leave page"
        cancelText="Stay"
        variant="warning"
      />
    </>
  );
};
